import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiImageUrl } from '../services/api';
import './Navigation.css';

const Navigation = ({ currentSection, starCount = 0, currentPlayer = null }) => {
  const { t, language, changeLanguage, availableLanguages, loading, debug } = useLocalization();
  const { user } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Map routes to sections
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.startsWith('/dragon-lair')) return 'dragonLair';
    if (path.startsWith('/profile')) return 'profile';
    if (path.startsWith('/admin')) return 'admin';
    return 'modules';
  };

  // Debug logging for language state
  React.useEffect(() => {
    console.log('🧭 Navigation language state:', {
      language,
      loading,
      debug
    });
  }, [language, loading, debug]);

  // Determine if user is admin (this would come from user data)
  const isAdmin = user?.role === 'admin';

  // Function to generate color from filename hash
  const getAvatarColor = (filename) => {
    if (!filename) return '#667eea';
    let hash = 0;
    for (let i = 0; i < filename.length; i++) {
      const char = filename.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Generate HSL color with higher saturation and better contrast
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 85%, 75%)`;
  };

  const navigationItems = [
    { key: 'modules', label: t('navigation.modules'), path: '/play' },
    { key: 'dragonLair', label: t('navigation.dragonLair'), path: '/dragon-lair' },
    { 
      key: 'profile', 
      label: t('navigation.profile'),
      path: '/profile',
      showAvatar: true
    },
  ];

  if (isAdmin) {
    navigationItems.push({ key: 'admin', label: t('navigation.adminArea'), path: '/admin', icon: '⚙️' });
  }
  
  const activeSection = getActiveSection();

  const handleMenuItemClick = () => {
    setIsMenuOpen(false); // Close menu after navigation
  };

  return (
    <header className="bg-white shadow-sm">
      <nav className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-decoration-none">
              <h2 className="text-xl font-bold text-blue-600 mb-0" style={{ fontFamily: 'Thicccboi, sans-serif' }}>Poligon</h2>
            </Link>
          </div>
          
          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map(item => (
              <Link
                key={item.key}
                to={item.path}
                className={`text-sm font-medium transition-colors uppercase tracking-wider ${
                  activeSection === item.key 
                    ? 'text-blue-600 border-b-2 border-blue-600 pb-1' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
                style={{ fontFamily: 'Thicccboi, sans-serif' }}
              >
                {item.showAvatar ? (
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-medium"
                      style={{ backgroundColor: currentPlayer ? getAvatarColor(currentPlayer.avatar || 'default') : '#667eea' }}
                    >
                      {currentPlayer && currentPlayer.avatar && currentPlayer.avatar.endsWith('.png') ? (
                        <img 
                          src={getApiImageUrl(`/api/avatars/${currentPlayer.avatar}`)} 
                          alt={currentPlayer.first_name}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                      ) : currentPlayer ? (
                        <span>{currentPlayer.avatar || '👦'}</span>
                      ) : (
                        <span>👦</span>
                      )}
                    </div>
                    <span>{item.label}</span>
                  </div>
                ) : (
                  <span>{item.label}</span>
                )}
              </Link>
            ))}
          </div>
          
          {/* Right side - About, Language & Stars */}
          <div className="flex items-center space-x-6">
            {/* About Link - Hidden on mobile */}
            <Link 
              to="/about"
              className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors uppercase tracking-wider"
              style={{ fontFamily: 'Thicccboi, sans-serif' }}
            >
              {t('navigation.about')}
            </Link>
            
            {/* Language Selector - Hidden on mobile */}
            <div className="hidden md:block nav-item dropdown language-select text-uppercase">
              <button 
                className="nav-link dropdown-item dropdown-toggle border-0 bg-transparent text-sm font-medium text-gray-700 hover:text-blue-600 uppercase tracking-wider transition-colors"
                style={{ fontFamily: 'Thicccboi, sans-serif' }}
                 
                data-bs-toggle="dropdown" 
                aria-haspopup="true" 
                aria-expanded="false"
                disabled={loading || !language}
              >
                {loading ? '...' : (language ? language.toUpperCase() : '...')}
              </button>
              <ul className="dropdown-menu">
                {availableLanguages.map(lang => (
                  <li key={lang.code} className="nav-item">
                    <button
                      className={`dropdown-item border-0 bg-transparent w-100 text-start uppercase ${
                        language === lang.code ? 'text-blue-600 font-bold' : ''
                      }`}
                      style={{ fontFamily: 'Thicccboi, sans-serif' }}
                      onClick={() => changeLanguage(lang.code)}
                      disabled={loading}
                    >
                      {lang.code.toUpperCase()} {language === lang.code ? '✓' : ''}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Stars - Always visible */}
            <div className="flex items-center space-x-2 text-yellow-500 font-semibold">
              <span className="text-lg">⭐</span>
              <span style={{ fontFamily: 'Thicccboi, sans-serif' }}>{starCount}</span>
            </div>
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden flex flex-col justify-center w-6 h-6 text-gray-600 hover:text-blue-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className={`block w-6 h-0.5 bg-current transition-transform ${isMenuOpen ? 'rotate-45 translate-y-1.5' : 'mb-1'}`}></span>
              <span className={`block w-6 h-0.5 bg-current transition-opacity ${isMenuOpen ? 'opacity-0' : 'mb-1'}`}></span>
              <span className={`block w-6 h-0.5 bg-current transition-transform ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu - Collapsible */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="mt-4 border-t border-gray-200 pt-4 space-y-2">
              {navigationItems.map(item => (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`block w-full px-3 py-3 text-left transition-colors uppercase tracking-wider font-medium ${
                    activeSection === item.key 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  style={{ fontFamily: 'Thicccboi, sans-serif' }}
                  onClick={handleMenuItemClick}
                >
                  {item.showAvatar ? (
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm text-white font-medium"
                        style={{ backgroundColor: currentPlayer ? getAvatarColor(currentPlayer.avatar || 'default') : '#667eea' }}
                      >
                        {currentPlayer && currentPlayer.avatar && currentPlayer.avatar.endsWith('.png') ? (
                          <img 
                            src={getApiImageUrl(`/api/avatars/${currentPlayer.avatar}`)} 
                            alt={currentPlayer.first_name}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        ) : currentPlayer ? (
                          <span>{currentPlayer.avatar || '👦'}</span>
                        ) : (
                          <span>👦</span>
                        )}
                      </div>
                      <span>{item.label}</span>
                    </div>
                  ) : (
                    <span>{item.label}</span>
                  )}
                </Link>
              ))}
            </div>
            
            {/* About Link - Mobile */}
            <Link 
              to="/about"
              className="block w-full px-3 py-3 text-left transition-colors uppercase tracking-wider font-medium text-gray-700 hover:bg-gray-50"
              style={{ fontFamily: 'Thicccboi, sans-serif' }}
              onClick={handleMenuItemClick}
            >
              {t('navigation.about')}
            </Link>
            
            {/* Mobile Language Selector - At bottom */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500 mb-3" style={{ fontFamily: 'Thicccboi, sans-serif' }}>
                {loading ? 'Loading...' : t('landing.selectLanguage')}
              </div>
              <div className="flex space-x-3">
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    className={`px-4 py-2 rounded text-sm font-medium transition-colors uppercase tracking-wider ${
                      language === lang.code 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ fontFamily: 'Thicccboi, sans-serif' }}
                    onClick={() => !loading && changeLanguage(lang.code)}
                    disabled={loading}
                  >
                    {lang.code.toUpperCase()} {language === lang.code ? '✓' : ''}
                  </button>
                ))}
              </div>
              
              {/* Debug info in development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-3 text-xs text-gray-400">
                  Debug: Lang={language || 'null'}, Loading={loading ? 'yes' : 'no'}, Keys={debug?.translationKeys || 0}
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navigation;